import React, { Component, type ReactNode } from 'react';
import { Box, Button, Heading, Text, VStack } from '@chakra-ui/react';
import { MaterialIcon } from './ui/MaterialIcon';
import { StatusBanner } from './ui/StatusBanner';
import { logger } from '../services/logging.service';
import { safeAnalytics } from '../utils/analytics';
import { isChunkLoadError, staleChunkReloadCount } from '../utils/lazyWithRetry';
import { ANALYTICS_EVENTS } from '../constants/analytics';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  level?: 'app' | 'route' | 'component';
}

const STALE_CHUNK_TITLE = 'Reload to continue';
// Hedged on BOTH clauses, because `isChunkLoadError` is deliberately broader
// than staleness: it also matches an offline dynamic import and the
// `ChunkLoadError` that `lazyWithRetry` mints for its own active-load timeout,
// which /m/workspace opts into. Narrowing the predicate for this branch would
// make the copy true by construction and reintroduce the dead end it exists to
// remove, since a timed-out route is cached-rejected by React.lazy exactly like
// a purged one.
const STALE_CHUNK_DESCRIPTION =
  'Part of the app could not load. That is usually a new version, sometimes a slow connection. Reloading usually fixes it.';

const BOUNDARY_TITLES: Record<NonNullable<Props['level']>, string> = {
  app: 'Application Error',
  route: 'Page Error',
  component: 'Component Error',
};

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ error, errorInfo });

    // Log the VERDICT, not just the evidence. `isChunkLoadError` keys almost
    // entirely on the error MESSAGE, and the message is deliberately not
    // forwarded to Cloud Logging (`logs.controller.ts` — an error message is
    // the classic UGC carrier), so nobody reading the log line can re-derive
    // the classification this component already computed. Without it a burst
    // of `errorName: TypeError` during a deploy is untriageable: a browser
    // throws exactly that for a failed dynamic import, and so does a genuine
    // crash. Three deploys inside an hour on 2026-08-25 produced 38 of them,
    // and "was that my deploy or a regression?" could only be answered by
    // guessing at the unlogged message.
    const isStaleChunk = isChunkLoadError(error);

    // `handledReloadPrompt`, not `staleChunk`: `isChunkLoadError` is
    // deliberately BROADER than staleness (see its docblock — it also matches
    // an offline dynamic import and the `ChunkLoadError` lazyWithRetry mints
    // for its own active-load timeout), so a field claiming a stale deploy
    // would be wrong for two of the cases that set it. What every branch of
    // `render()` below does guarantee is the prompt, so name the field after
    // that: true by construction, and it is what the triage question actually
    // needs — did the user get a recoverable screen, or a dead end?
    const metadata = {
      level: this.props.level,
      errorName: error.name,
      handledReloadPrompt: isStaleChunk,
      staleChunkReloadCount: staleChunkReloadCount(),
      errorMessage: error.message,
      errorStack: error.stack,
      componentStack: errorInfo.componentStack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      // Include additional error properties if they exist
      ...((error as any).code && { errorCode: (error as any).code }),
      ...((error as any).cause && { errorCause: (error as any).cause }),
    };

    if (isStaleChunk) {
      // WARN, not ERROR: this hit rendered "Reload to continue" and one click
      // cures it. An ERROR stream that carries recovered prompts is an ERROR
      // stream people stop reading, which is the failure mode rule 12 warns
      // about — and a deploy is exactly when it needs to be readable.
      //
      // `flushNow` because the batcher only auto-flushes on ERROR; a WARN
      // waits up to 30s, and the next thing this user does is replace the
      // document. Fire-and-forget, same best-effort contract as the
      // `beforeunload` flush.
      logger.warn('React component error boundary triggered', {
        component: 'ErrorBoundary',
        operation: 'componentDidCatch',
        metadata,
      });
      void logger.flushNow();
    } else {
      // Pass error as second parameter so logging service extracts stack/message
      logger.error('React component error boundary triggered', error, {
        component: 'ErrorBoundary',
        operation: 'componentDidCatch',
        metadata,
      });
    }

    // Capture to PostHog error tracking
    safeAnalytics.captureException(error, {
      boundary_level: this.props.level,
      component_stack: errorInfo.componentStack,
    });

    // Log error to console in development (already handled by logging service)
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  /**
   * Serves both reload buttons, so it must not claim a cause it doesn't know:
   * only the stale-chunk path is measured, and `logger.info` is deliberately
   * absent — the log batcher would not flush before the document goes.
   *
   * Reaching this button at all means `lazyWithRetry`'s one-shot auto-reload
   * did not cure the failure, which is the signal that somebody is genuinely
   * stranded — so `reload_count` rides along, matching the automatic leg and
   * answering how many attempts had already failed. `trackImmediate` is an
   * alias for `track`; what actually gets this out is posthog-js's own
   * pagehide flush. Best-effort either way.
   */
  handleReload = () => {
    if (isChunkLoadError(this.state.error)) {
      safeAnalytics.trackImmediate(ANALYTICS_EVENTS.STALE_CHUNK_RELOADED, {
        trigger: 'manual',
        boundary_level: this.props.level ?? 'component',
        reload_count: staleChunkReloadCount(),
      });
    }

    globalThis.location.reload();
  };

  handleReset = () => {
    // Log recovery attempt
    logger.info('User attempting error boundary recovery', {
      component: 'ErrorBoundary',
      operation: 'handleReset',
      metadata: {
        level: this.props.level,
        previousError: this.state.error?.message,
      },
    });

    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      const { level = 'component' } = this.props;
      // The one error class this boundary cannot retry out of: `React.lazy`
      // caches a rejected import permanently, so resetting state re-renders
      // the same lazy route and re-throws the identical error. The crash
      // telemetry for that looks like somebody pressing "Try Again" over and
      // over (PostHog 019cd9cd…, 20 crashes in four minutes on /m/workspace).
      // Only a fresh document recovers, because only that re-fetches an
      // index.html pointing at chunks that still exist.
      const isStaleChunk = isChunkLoadError(this.state.error);

      // A custom fallback decides how to PRESENT a failure; it must not be
      // able to opt out of the only recovery that works for stale code. All
      // three fallbacks in the app suppress it by accident rather than by
      // intent: the outermost route boundary (App.tsx) catches every one of
      // the ~33 routes that lack their own boundary and offers only a homepage
      // link, and the two Bird boundaries render a dead notice. So the
      // stale-chunk branch outranks the prop.
      if (this.props.fallback && !isStaleChunk) {
        return this.props.fallback;
      }

      // A component boundary can be one of many on screen — chatCode wraps one
      // per code fence, BirdMessage one per chart — so the page-shaped panel
      // below would repeat down a chat column for a single global cause.
      if (isStaleChunk && level === 'component') {
        return (
          <StatusBanner
            tone="warning"
            title={STALE_CHUNK_TITLE}
            description={STALE_CHUNK_DESCRIPTION}
            action={{ label: 'Reload', onClick: this.handleReload }}
          />
        );
      }

      return (
        <Box p={6} maxWidth="600px" mx="auto">
          <StatusBanner
            // 'warning' not 'info': StatusBanner derives the live region from
            // tone, and 'info' is polite. A newly inserted polite region on a
            // state that has replaced the route content is the least reliably
            // announced combination there is.
            tone={isStaleChunk ? 'warning' : 'error'}
            title={isStaleChunk ? STALE_CHUNK_TITLE : BOUNDARY_TITLES[level]}
            description={
              isStaleChunk
                ? STALE_CHUNK_DESCRIPTION
                : "Something went wrong. We've been notified and will fix this as soon as possible."
            }
          />

          <VStack mt={6} spacing={4}>
            {isStaleChunk ? (
              <>
                <Button leftIcon={<MaterialIcon name="refresh" />} onClick={this.handleReload}>
                  Reload
                </Button>

                {/* A full document navigation, and to a DIFFERENT route: the
                    escape RouteErrorFallback offered these ~33 routes before
                    this branch outranked it. Worth keeping, because the
                    homepage chunk may survive a deploy this route's did not. */}
                <Button as="a" href="/" variant="outline">
                  Go to Homepage
                </Button>
              </>
            ) : (
              <>
                <Button leftIcon={<MaterialIcon name="refresh" />} onClick={this.handleReset}>
                  Try Again
                </Button>

                {level === 'app' && (
                  <Button variant="outline" onClick={this.handleReload}>
                    Reload Page
                  </Button>
                )}
              </>
            )}
          </VStack>

          {process.env.NODE_ENV === 'development' && this.state.error && (
            <Box mt={6} p={4} bg="bg.subtle" borderRadius="md" fontSize="sm">
              <Heading color="text.primary" size="sm" mb={2}>
                Debug Information:
              </Heading>
              <Text fontFamily="mono" color="text.error" whiteSpace="pre-wrap">
                {this.state.error.toString()}
              </Text>
              {this.state.errorInfo && (
                <Text fontFamily="mono" color="text.secondary" whiteSpace="pre-wrap" mt={2}>
                  {this.state.errorInfo.componentStack}
                </Text>
              )}
            </Box>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}

// Specific error fallbacks for different contexts
export const ComponentErrorFallback = ({ onReset }: { onReset?: () => void }) => (
  <StatusBanner
    tone="warning"
    title="Component Unavailable"
    description="This component encountered an error and cannot be displayed."
    action={onReset ? { label: 'Retry', onClick: onReset } : undefined}
  />
);

export const RouteErrorFallback = ({ onReset }: { onReset?: () => void }) => (
  <Box textAlign="center" py={10} px={6}>
    <Heading display="inline-block" as="h2" size="2xl" bg="danger.400" backgroundClip="text">
      Oops!
    </Heading>
    <Text color="text.primary" fontSize="18px" mt={3} mb={2}>
      Page Error
    </Text>
    <Text color="text.secondary" mb={6}>
      This page encountered an error and cannot be displayed properly.
    </Text>
    <VStack spacing={3}>
      {/* `primary`, and deliberately NOT one of the `danger` variants. Retrying
          is a recovery action, not a destructive one — the red this button used
          to ask for (via a `colorScheme` that never reached it) was signalling
          "an error happened", which the heading already says, rather than "this
          will destroy something". `danger` is reserved for the latter. */}
      {onReset && (
        <Button variant="primary" onClick={onReset}>
          Try Again
        </Button>
      )}
      <Button as="a" href="/" variant="outline">
        Go to Homepage
      </Button>
    </VStack>
  </Box>
);
